import React, { useState } from 'react';
import { Button, Container, Stack, Tabs, Tab, Box } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Page } from '../../../../components';
import HeaderBreadcrumbs from '../../../../components/header-breadcrumbs';
import { SystemUserList } from '../../../../components/lists/system-user-list';
import { ComponentPermissionGateway } from '../../../../components/component-permission-gateway';
import { customerPermissionFlags } from '../../../../constants/systemPermissions.constants';
import { RootState } from '../../../../store';
import { MobileAppUsersList } from '../../../../components/lists/mobile-app-users-list';
import { GT_COLORS } from '../../../../constants/designTokens.constants';

export const SystemUserListScreen: React.FC = () => {
    const { authUser } = useSelector((state: RootState) => state.auth);
    const [currentTab, setCurrentTab] = useState('Web App Users');

    const navigate = useNavigate();

    // Bulk upload flow is gated the same way it was inside the filter accordion:
    // hidden for PomMasters who aren't masquerading, since bulk upload assumes
    // a single customer context.
    const showBulkUploadExperience = !authUser?.pomMaster || authUser?.masquarade;

    const TABS = [
        {
            value: 'Web App Users',
            content: <SystemUserList />,
        },
        ...(authUser?.pomMaster ? [{
            value: 'Mobile App Users',
            content: <MobileAppUsersList />,
        }] : []),
    ];

    const currentTabData = TABS.find(tab => tab.value === currentTab);

    return (
        <Page title="POM - System Users">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <HeaderBreadcrumbs
                        heading="System Users"
                        links={[
                            {
                                name: authUser?.pomMaster ? 'Listing all the system users on POM. Those users are from various companies.' :'Your subscripition system users list.',
                                href: '/system-users'
                            },
                        ]}
                        activeLast={false}
                    />
                    {currentTab === 'Web App Users' && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <ComponentPermissionGateway role={customerPermissionFlags.TEAM_LEADER}>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/system-users/create')}
                                    sx={{
                                        backgroundColor: GT_COLORS.primary,
                                        textTransform: 'none',
                                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                    }}
                                >
                                    {authUser?.pomMaster && !authUser?.masquarade ? 'Add New POM Master' : 'Add New System User'}
                                </Button>
                            </ComponentPermissionGateway>
                            {showBulkUploadExperience && (
                                <ComponentPermissionGateway role={customerPermissionFlags.ADMIN}>
                                    <Button
                                        variant="outlined"
                                        endIcon={<UploadFileIcon />}
                                        onClick={() => navigate('/system-users/bulk-upload')}
                                        sx={{
                                            color: GT_COLORS.primary,
                                            borderColor: GT_COLORS.primary,
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: GT_COLORS.primaryHover,
                                                backgroundColor: `${GT_COLORS.primary}08`,
                                            },
                                        }}
                                    >
                                        Bulk Upload
                                    </Button>
                                </ComponentPermissionGateway>
                            )}
                        </Stack>
                    )}
                </Stack>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs 
                        value={currentTab} 
                        onChange={(e, value) => setCurrentTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {TABS.map((tab) => (
                            <Tab 
                                key={tab.value} 
                                label={tab.value} 
                                value={tab.value}
                                sx={{ fontSize: 14, fontWeight: 500 }}
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box>
                    {currentTabData?.content}
                </Box>
            </Container>
        </Page>
    )
}